import React, { CSSProperties, useEffect, useRef, useState } from "react"
import "./renamable-label.css"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { useTranslation } from "@nova/use-translation"
import cn from "classnames"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"

type RenamableLabelProps = {
    value: string
    rename: boolean
    highlightOnRenameStart?: boolean
    setRename: (rename: boolean) => void
    onRename: (value: string) => void
    onInputChange?: (value: string) => void
    placeholder?: string
    autoFocus?: boolean
    enableClickAway?: boolean
    height?: number
    classNames?: string
    inputClassNames?: string
    style?: CSSProperties
}
export const RenamableLabel = ({
    value,
    rename,
    highlightOnRenameStart,
    setRename,
    placeholder,
    onRename,
    height = 37,
    classNames,
    inputClassNames,
    style,
}: RenamableLabelProps) => {
    const { t } = useTranslation()
    const [label, setLabel] = useState(value)
    const inputRef = useRef<HTMLInputElement>(null)
    const [inputText, setInputText] = useState(label)

    const confirmRename = () => {
        // If the user hasn't changed anything then just leave the name as it is.
        if (inputText.length === 0) {
            setRename(false)
            setInputText(label)
            return
        }
        setLabel(inputText)
        setRename(false)
        onRename(inputText)
    }

    // Confirm rename if this component forcibly exited the renaming state via its parent.
    const isRenamingRef = useRef(false)
    useEffectUnsafe(() => {
        if (rename) {
            isRenamingRef.current = true
            if (highlightOnRenameStart) {
                inputRef.current?.setSelectionRange(0, inputText.length)
            }
            return
        } else if (isRenamingRef.current) {
            isRenamingRef.current = false
            confirmRename()
        }
    }, [rename])

    // Focus on the input when rename is selected
    useEffectUnsafe(() => {
        if (inputRef.current) {
            inputRef.current.focus()
        }
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return
            setRename(false)
            setInputText(label)
        }
        window.removeEventListener("keydown", closeOnEscape)
        window.addEventListener("keydown", closeOnEscape)
        return () => {
            window.removeEventListener("keydown", closeOnEscape)
        }
    }, [rename, label])

    useEffect(() => {
        setLabel(value)
        setInputText(value)
    }, [value])

    return (
        <>
            {!rename && (
                <small
                    className={cn("text-nova-soft-white w-full truncate", classNames)}
                    style={style}
                >
                    {t(label)}
                </small>
            )}
            {rename && (
                <ClickAwayListener onClickAway={confirmRename} className="w-full">
                    <form
                        onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
                            event.preventDefault()
                            event.stopPropagation()
                            confirmRename()
                        }}
                        className="w-full"
                    >
                        <input
                            type="text"
                            className={cn(
                                "text-nova-white !bg-transparent  border-none focus:outline-none outline-none focus:border-none !text-[12px] w-full !border-transparent focus:border-transparent focus:ring-0 !pl-0",
                                inputClassNames
                            )}
                            ref={inputRef}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setInputText((event.target as HTMLInputElement).value)
                            }}
                            onKeyDown={(e) => {
                                e.stopPropagation()
                            }}
                            placeholder={placeholder}
                            value={inputText}
                            style={{ height, paddingLeft: 0, color: style?.color }}
                        />
                    </form>
                </ClickAwayListener>
            )}
        </>
    )
}
